import React, { RefObject, useEffect, useState } from 'react'
import { Col, Form, Row, Select as SelectAntd } from 'antd'
import { FormikProps } from 'formik'
import { useDispatch, useSelector } from 'react-redux'

import { ApplicationState } from 'src/store'
import { Gender, Request, RequestTicket } from 'src/types'
import { getUsers } from 'src/core/store/ducks/users/actions'
import { InputCurrency, Select, SelectResource } from 'src/components/Formik'
import { Option } from 'src/components/Formik/Select'
import { useT } from 'src/core/hooks/use-translation'
import currencyMask from 'src/components/Formik/InputCurrency/mask'

import { CostReais } from '../../styles'

type Props = {
  formikProps: FormikProps<RequestTicket>
  refReceiverIdSelect: RefObject<SelectAntd<string>>
  request: Request
}

interface UserOption extends Option {
  gender?: Gender
}

function FormFields({
  formikProps,
  refReceiverIdSelect,
  request
}: Props): JSX.Element {
  const { t } = useT()
  const dispatch = useDispatch()
  const { resources, users } = useSelector((state: ApplicationState) => ({
    resources: state.resources,
    users: state.users
  }))
  const [userList, setUserList] = useState<UserOption[]>([])

  useEffect(() => {
    if (!users.list.length) dispatch(getUsers())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setUserList([
      { label: t('web.services.advances.form.receiver.everyone'), value: '*' },
      ...request.request_user_data.map(item => ({
        gender: item.traveler.gender,
        label: item.traveler.name,
        value: item.traveler.user_id.toString()
      }))
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.request_user_data])

  function handleRenderExchange(): JSX.Element {
    const foundCurrency = resources.currencies.list.find(
      item =>
        +item.currency_id ===
        +formikProps.values.request_ticket_title_advances[0].currency_id
    )
    return (
      <Col span={4}>
        <InputCurrency
          dataTestId="advance-rate"
          disabled={foundCurrency && foundCurrency.code === 'BRL'}
          formikProps={formikProps}
          label="web.services.advances.form.exchange-rate.title"
          name="request_ticket_title_advances.0.exchange_rate"
          placeholder="web.services.advances.form.exchange-rate.placeholder"
          precision={4}
        />
      </Col>
    )
  }

  return (
    <>
      <Row gutter={24}>
        <Col span={8}>
          <Select
            autoFocus
            dataTestId="receiver"
            disabled={!!formikProps?.values?.request_ticket_id}
            formikProps={formikProps}
            innerRef={refReceiverIdSelect}
            label="web.services.advances.form.receiver.placeholder"
            loading={users.loading}
            name="receiver_id"
            options={userList}
          />
        </Col>
        <Col span={6}>
          <SelectResource
            formikProps={formikProps}
            keyLabel="code"
            keyValue="currency_id"
            label="web.services.advances.form.currency.title"
            name="request_ticket_title_advances.0.currency_id"
            organization_id={request.organization_id}
            placeholder="web.services.advances.form.currency.placeholder"
            resource="currencies"
          />
        </Col>
        <Col span={4}>
          <InputCurrency
            dataTestId="advance-amount"
            formikProps={formikProps}
            label="web.services.advances.form.amount.title"
            name="request_ticket_title_advances.0.amount"
            placeholder="web.services.advances.form.amount.placeholder"
          />
        </Col>
        {handleRenderExchange()}
      </Row>
      <Row gutter={24}>
        <Col span={8}>
          <SelectResource
            formikProps={formikProps}
            keyLabel="name"
            keyValue="financial_resource_id"
            label="web.services.advances.form.resource.title"
            name="request_ticket_title_advances.0.financial_resource_id"
            organization_id={request.organization_id}
            placeholder="web.services.advances.form.resource.placeholder"
            resource="financial_resources"
          />
        </Col>
        <Col span={8}>
          <Form.Item
            colon={false}
            label={t('web.services.advances.form.cost-in-reais')}
          >
            <CostReais data-test-id="advance-total-cost" level={4}>
              {
                currencyMask({
                  formatStr: true,
                  value:
                    formikProps.values.request_ticket_title_advances[0].amount *
                    formikProps.values.request_ticket_title_advances[0]
                      .exchange_rate
                }).maskedValue
              }
            </CostReais>
          </Form.Item>
        </Col>
      </Row>
    </>
  )
}

export default FormFields
